<template>
  <div class="flex flex-col gap-3">
    <div class="overflow-x-auto">
      <table class="w-full border-collapse text-sm">
        <thead>
          <tr class="border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
            <th class="py-1 pr-3 font-semibold">PO</th>
            <th class="py-1 pr-3 font-semibold">Line</th>
            <th class="py-1 pr-3 font-semibold">Part</th>
            <th class="py-1 pr-3 text-right font-semibold">Planned pcs</th>
            <th class="py-1 text-right font-semibold">Packing list pcs</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.key" class="border-b border-gray-100" :class="row.differs ? 'bg-amber-50' : ''">
            <td class="py-1.5 pr-3 font-semibold text-gray-900">{{ row.poNumber }}</td>
            <td class="py-1.5 pr-3 tabular-nums text-gray-500">{{ row.lineNos.join(', ') || '—' }}</td>
            <td class="break-all py-1.5 pr-3 font-mono text-gray-900">{{ row.partNumber ?? '—' }}</td>
            <td class="py-1.5 pr-3 text-right tabular-nums">{{ fmtPcs(row.planned) }}</td>
            <td class="py-1.5 text-right tabular-nums" :class="row.differs ? 'font-semibold text-amber-800' : ''">
              {{ fmtPcs(row.listed) }}
              <span v-if="row.differs" class="ml-1 text-xs">{{ pcsDeltaText(row.listed, row.planned) }}</span>
            </td>
          </tr>
          <tr v-if="rows.length === 0">
            <td colspan="5" class="py-2 text-gray-500">No lines to compare.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="unmatched.length">
      <p class="m-0 text-sm font-semibold text-gray-900">Rows not matched to a line</p>
      <ul class="m-0 mt-1 flex list-none flex-col gap-1 p-0 text-xs">
        <!-- Below sm each row stacks under its row number. -->
        <li
          v-for="r in unmatched"
          :key="r.rowNumber"
          class="grid grid-cols-[64px_minmax(0,1fr)] gap-x-3 gap-y-0.5 sm:grid-cols-[64px_100px_minmax(0,1fr)_80px_minmax(0,1fr)] sm:gap-y-0"
        >
          <span class="tabular-nums text-gray-500 max-sm:row-span-4">Row {{ r.rowNumber }}</span>
          <span class="text-gray-900">{{ r.poNumber ?? '—' }}</span>
          <span class="break-all font-mono text-gray-900">{{ r.partNumber ?? '—' }}</span>
          <span class="tabular-nums sm:text-right">
            {{ r.quantity === null ? '—' : fmtPcs(r.quantity) }}
            <span class="sm:hidden">pcs</span>
          </span>
          <span class="text-amber-800">{{ r.unmatchedReason ? unmatchedReasonText(r.unmatchedReason) : '' }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';

  import type { PackingList, PoDetail, Shipment } from '@/types/poTracker';
  import { comparePackingList, fmtPcs, pcsDeltaText, unmatchedReasonText } from '@/utils/poTracker';

  const props = defineProps<{ packingList: PackingList; shipments: Shipment[]; poDetails: Pick<PoDetail, 'poNumber' | 'lines'>[] }>();

  const rows = computed(() => comparePackingList(props.shipments, props.packingList.rows, props.poDetails));
  const unmatched = computed(() => props.packingList.rows.filter((r) => r.odooLineId === null));
</script>
